import { FunctionComponent, useCallback, useMemo } from "react"
import { observer } from "mobx-react-lite"
import { makeStyles } from '@griffel/react'
import { Button } from "@fluentui/react-button"
import { GenericList, useGenericListSettings, getParamId, PARAM_ORDER } from "@knowmax/genericlist-core"
import { LANGUAGE_LABELRESET } from "../../types/language"

const useClasses = makeStyles({
  button: {
    width: '100%'
  }
})

export const ResetFilters: FunctionComponent<{ list?: GenericList, listId?: string }> = observer(({ list, listId }) => {
  const classes = useClasses()
  const settings = useGenericListSettings()

  const hasfilter = useMemo(() => {
    if (list?.filterList) {
      // Iterate through the filterList to check if any filter has a value set in params
      const hasActiveFilter = list.filterList.some(filter => {
        const paramId = getParamId(filter.id, listId)
        return settings.params.has(paramId)
      })
      if (hasActiveFilter) return true
      // Also show the reset button when a non-default order has been selected
      return settings.params.has(getParamId(PARAM_ORDER, listId))
    } else if (listId !== undefined && listId !== '') {
      for (const key of settings.params.keys()) {
        if (key.startsWith(listId + '.')) {
          return true
        }
      }

      return false
    } else {
      return settings.params.toString() !== ''
    }
  }, [settings.params, listId, list?.filterList])

  const reset = useCallback(() => {
    if (listId !== undefined && listId !== '') {
      // Only remove everything prefixed with listId.
      // Snapshot the matching keys first — deleting during iteration shifts indices
      // and causes subsequent entries to be skipped (e.g. the order param).
      const params = new URLSearchParams(settings.params);
      const keysToDelete = Array.from(params.keys()).filter(key => key.startsWith(listId + '.'));

      keysToDelete.forEach(key => params.delete(key));

      if (keysToDelete.length > 0) {
        settings.serialize(params);
      }
    } else {
      if (settings.params.toString() !== '') {
        settings.serialize(new URLSearchParams())
      }
    }

    // deserializeOrder (in @knowmax/genericlist-core) does not reset list.order when the
    // order param is absent, so list.hash never changes and list.load() is never triggered.
    // Resetting the order directly ensures the hash changes and triggers a data reload.
    if (list) {
      const defaultOrder = list.orderList?.find(o => o.default === true)
      list.setOrder(defaultOrder)
    }
  }, [settings, list])

  return (
    <>
      {hasfilter &&
        <Button appearance='primary' className={classes.button} onClick={reset}>{settings.onTranslate(LANGUAGE_LABELRESET)} </Button>
      }
    </>
  )
})